import { useState, useEffect } from 'react';
import { X, CheckCircle, XCircle, Award, Loader2, FileText } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

/**
 * Admin modal showing the full progress report for a single student
 * across all of their online-course enrollments. Loaded from
 * GET /api/admin/online-courses/student-progress/{user_id}.
 *
 * Props:
 *  - userId (required)
 *  - onClose (required)
 *  - onWaive (optional callback when an exam is waived, so the table reloads)
 */
const StudentProgressPanel = ({ userId, onClose, onWaive }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API}/api/admin/online-courses/student-progress/${userId}`);
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${r.status}`);
      }
      const d = await r.json();
      setData(d);
    } catch (e) {
      setError(e.message || 'Failed to load progress');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (userId) load(); }, [userId]);

  const waive = async (course, examType) => {
    const reason = prompt(`Reason for waiving ${examType} for ${course.course_title}? (optional)`) || '';
    setBusy(true);
    try {
      const r = await fetch(
        `${API}/api/admin/online-courses/${course.course_id}/waive-exam/${userId}?exam_type=${examType}&reason=${encodeURIComponent(reason)}`,
        { method: 'POST' }
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        alert(`Waiver failed: ${body.detail || r.status}`);
        return;
      }
      await load();
      if (onWaive) onWaive();
    } finally {
      setBusy(false);
    }
  };

  const waiveEnrollment = async (course) => {
    const reason = prompt(`Reason for waiving the $99 enrollment fee for ${course.course_title}? (optional)`) || '';
    setBusy(true);
    try {
      const r = await fetch(
        `${API}/api/admin/online-courses/${course.course_id}/waive-enrollment/${userId}?reason=${encodeURIComponent(reason)}`,
        { method: 'POST' }
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        alert(`Waiver failed: ${body.detail || r.status}`);
        return;
      }
      await load();
      if (onWaive) onWaive();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="student-progress-modal"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14, width: 'min(960px, 100%)',
          maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
        }}
      >
        {/* header */}
        <div style={{
          background: 'linear-gradient(135deg,#0b2545 0%,#13315c 100%)',
          color: '#fff', padding: '18px 22px',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <FileText size={22} color="#f7d774" />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '1.05rem' }} data-testid="student-progress-title">
              Student Progress {data?.student?.full_name ? `— ${data.student.full_name}` : ''}
            </div>
            {data?.student?.email && (
              <div style={{ fontSize: '0.8rem', opacity: 0.85 }}>{data.student.email}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="student-progress-close"
            style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}
          >
            <X size={22} />
          </button>
        </div>

        {/* body */}
        <div style={{ padding: 22 }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
              <Loader2 className="spin" size={28} />
              <p style={{ marginTop: 8 }}>Loading progress…</p>
            </div>
          )}
          {error && !loading && (
            <div style={{ background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', padding: 12, borderRadius: 8 }}>
              {error}
            </div>
          )}

          {!loading && !error && data && data.courses.length === 0 && (
            <div style={{ textAlign: 'center', color: '#64748b', padding: 30 }}>
              This student has no online enrollments yet.
            </div>
          )}

          {!loading && !error && data && data.courses.map((c) => (
            <div
              key={c.enrollment_id}
              data-testid={`progress-course-${c.course_id}`}
              style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, marginBottom: 16 }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 700, color: '#0b2545' }}>{c.course_title}</div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                    Apprentice ID: {c.apprentice_id} · Enrolled: {c.enrolled_at ? new Date(c.enrolled_at).toLocaleDateString() : '—'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span
                    data-testid={`progress-status-${c.course_id}`}
                    style={{
                      background: c.certificate_generated ? '#dcfce7' : '#fef3c7',
                      color: c.certificate_generated ? '#166534' : '#92400e',
                      padding: '4px 10px', borderRadius: 999,
                      fontWeight: 600, fontSize: '0.75rem',
                    }}
                  >
                    {c.certificate_generated ? 'Certificate Issued' : `${c.completion_percent}% complete`}
                  </span>
                  {c.certificate_generated && (
                    <a
                      href={`${API}/api/online-courses/${c.course_id}/certificate/${userId}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`progress-cert-${c.course_id}`}
                      style={{
                        background: '#22c55e', color: '#fff',
                        padding: '4px 10px', borderRadius: 6, textDecoration: 'none',
                        fontSize: '0.75rem', fontWeight: 600, display: 'inline-flex', gap: 4, alignItems: 'center',
                      }}
                    >
                      <Award size={12} /> View Cert
                    </a>
                  )}
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => waiveEnrollment(c)}
                    data-testid={`progress-waive-enrollment-${c.course_id}`}
                    style={{
                      background: '#0d9488', color: '#fff',
                      padding: '4px 10px', borderRadius: 6, border: 'none',
                      fontSize: '0.72rem', fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer',
                    }}
                    title="Waive the $99 program-enrollment fee"
                  >
                    Waive Enroll
                  </button>
                </div>
              </div>

              {/* progress bar */}
              <div style={{ height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden', marginBottom: 12 }}>
                <div style={{
                  height: '100%',
                  width: `${c.completion_percent}%`,
                  background: c.certificate_generated ? '#22c55e' : '#3b82f6',
                }} />
              </div>

              {/* exam table */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 12 }}>
                {['midterm', 'final_exam'].map((key) => {
                  const ex = c[key];
                  const examType = key === 'midterm' ? 'midterm' : 'final';
                  return (
                    <div
                      key={key}
                      data-testid={`progress-exam-${examType}-${c.course_id}`}
                      style={{ background: '#f8fafc', padding: 10, borderRadius: 8, fontSize: '0.82rem' }}
                    >
                      <div style={{ fontWeight: 700, color: '#0b2545', marginBottom: 4 }}>
                        {examType === 'midterm' ? 'Mid-Term' : 'Final Exam'}
                      </div>
                      <div>Paid: <strong>{ex.paid ? 'Yes' : 'No'}</strong>{ex.waived ? ' (waived)' : ''}</div>
                      <div>Passed: <strong>{ex.passed ? 'Yes' : 'No'}</strong></div>
                      <div>Score: <strong>{ex.score ?? '—'}{ex.score != null ? '%' : ''}</strong></div>
                      {!ex.paid && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => waive(c, examType)}
                          data-testid={`progress-waive-${examType}-${c.course_id}`}
                          style={{
                            marginTop: 6, fontSize: '0.7rem', padding: '4px 8px',
                            background: '#7c3aed', color: '#fff', border: 'none',
                            borderRadius: 4, cursor: busy ? 'not-allowed' : 'pointer', fontWeight: 600,
                          }}
                        >
                          Waive {examType}
                        </button>
                      )}
                    </div>
                  );
                })}
                <div
                  style={{ background: '#f8fafc', padding: 10, borderRadius: 8, fontSize: '0.82rem' }}
                  data-testid={`progress-clinical-${c.course_id}`}
                >
                  <div style={{ fontWeight: 700, color: '#0b2545', marginBottom: 4 }}>Clinical Form</div>
                  {c.clinical_form ? (
                    <>
                      <div>Status: <strong>{c.clinical_form.status}</strong></div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                        Uploaded: {c.clinical_form.uploaded_at ? new Date(c.clinical_form.uploaded_at).toLocaleDateString() : '—'}
                      </div>
                    </>
                  ) : (
                    <div style={{ color: '#64748b' }}>Not uploaded</div>
                  )}
                </div>
              </div>

              {/* per-module list */}
              {c.modules.length > 0 && (
                <details>
                  <summary style={{ cursor: 'pointer', fontSize: '0.8rem', color: '#475569', fontWeight: 600 }}>
                    {c.modules_completed}/{c.total_modules} modules · click to expand
                  </summary>
                  <div style={{ marginTop: 8, borderTop: '1px solid #e2e8f0', paddingTop: 8 }}>
                    {c.modules.map((m) => (
                      <div
                        key={m.module_number}
                        style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '6px 0', borderBottom: '1px dashed #e2e8f0', fontSize: '0.8rem',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {m.quiz_passed ? (
                            <CheckCircle size={14} color="#22c55e" />
                          ) : (
                            <XCircle size={14} color="#cbd5e1" />
                          )}
                          <span><strong>#{m.module_number}</strong> {m.title}</span>
                        </div>
                        <div style={{ color: '#64748b' }}>
                          {m.quiz_score != null ? `${m.quiz_score}%` : '—'} · {m.attempts || 0} attempts
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ))}
        </div>
      </div>
      <style>{`@keyframes ai-spin { to { transform: rotate(360deg); } } .spin { animation: ai-spin 1s linear infinite; }`}</style>
    </div>
  );
};

export default StudentProgressPanel;
